import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { formatDateOnly, formatDateTime } from '../utils/date';
import type { MinutePack } from '../types';

type PurchasedPackStatus = 'active' | 'consumed' | 'expired';

const statusColorMap: Record<PurchasedPackStatus, 'success' | 'default' | 'warning'> = {
  active: 'success',
  consumed: 'default',
  expired: 'warning',
};

function HeaderWithHelp({
  label,
  tooltip,
}: {
  label: string;
  tooltip?: string;
}) {
  if (!tooltip) {
    return <>{label}</>;
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <span>{label}</span>
      <Tooltip title={tooltip} arrow>
        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Stack>
  );
}

function resolvePackStatus(pack: MinutePack, nowMs: number): PurchasedPackStatus {
  const expiresAtMs = pack.expiresAt ? new Date(pack.expiresAt).getTime() : null;
  if (expiresAtMs !== null && expiresAtMs <= nowMs) {
    return 'expired';
  }
  if (pack.secondsRemaining <= 0 || !pack.isActive) {
    return 'consumed';
  }
  return 'active';
}

interface SubscriptionMinutePacksTableProps {
  packs: MinutePack[];
  title: string;
  subtitle?: string;
  emptyMessage: string;
}

export default function SubscriptionMinutePacksTable({
  packs,
  title,
  subtitle,
  emptyMessage,
}: SubscriptionMinutePacksTableProps) {
  const { t } = useTranslation(['pages']);
  const { locale, formatCurrency, formatDuration } = useNumberFormat();
  const nowMs = Date.now();

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        ) : null}
        {packs.length === 0 ? (
          <Typography color="text.secondary">{emptyMessage}</Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('pages:subscription.packsCard.columns.pack')}</TableCell>
                  <TableCell>{t('pages:subscription.packsCard.columns.status')}</TableCell>
                  <TableCell>{t('pages:subscription.packsCard.columns.usage')}</TableCell>
                  <TableCell>
                    <HeaderWithHelp
                      label={t('pages:subscription.packsCard.columns.balance')}
                      tooltip={t('pages:subscription.packsCard.tooltips.balance')}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packs.map((pack) => {
                  const status = resolvePackStatus(pack, nowMs);
                  const usedSeconds = Math.max(pack.secondsTotal - pack.secondsRemaining, 0);
                  return (
                    <TableRow key={pack.id} hover>
                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="body2" fontWeight={600}>
                            {t(`pages:subscription.packTypes.${pack.type}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:subscription.packsCard.purchased')}{' '}
                            {formatDateTime(pack.purchasedAt, {
                              locale,
                              options: { dateStyle: 'medium', timeStyle: 'short' },
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:subscription.packsCard.expires')}{' '}
                            {pack.expiresAt
                              ? formatDateOnly(pack.expiresAt, { locale })
                              : t('pages:subscription.packsCard.noExpiry')}
                            {' · '}
                            {formatCurrency(Number(pack.priceAmount), pack.priceCurrency, 2)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusColorMap[status]}
                          label={t(`pages:subscription.packsCard.status.${status}`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDuration(usedSeconds)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDuration(pack.secondsRemaining)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:subscription.packsCard.totalLabel', {
                              value: formatDuration(pack.secondsTotal),
                            })}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
